// 📈 마케팅 유입경로(inflow) 채널 상수 - 단일 소스 (Single Source of Truth)
// 배너 링크의 utm_source/ref 값과 referrer 도메인을 채널로 분류할 때 사용
// ⚠️ 기존 channel(b2b/b2c), source_type(direct/manual/partner)과는 별개 개념

/**
 * 사전 정의된 유입 채널 값 배열 (타입 추론용)
 * - utm_source 값은 여기 없는 값도 허용됨 (raw 값 그대로 저장/표시)
 * - 새 채널은 링크에 새 utm_source를 쓰기만 하면 자동 집계됨
 */
export const INFLOW_CHANNEL_VALUES = [
  'gmarket',
  'auction',
  'naver',
  'coupang',
  'st11',
  'google',
  'youtube',
  'instagram',
  'facebook',
  'kakao',
  'daum',
  'direct',
  'referral',
] as const;

export type InflowChannelValue = typeof INFLOW_CHANNEL_VALUES[number];

/** 표시 전용 버킷: 유입 정보가 없는 문서 (기능 배포 이전 레거시 포함) */
export const INFLOW_BUCKET_UNKNOWN = 'unknown';
/** 표시 전용 버킷: 관리자 수동/거래처 등록 건 (utm 수집 대상 아님) */
export const INFLOW_BUCKET_MANUAL = 'manual_admin';

/**
 * 📝 채널별 한글 라벨
 */
export const INFLOW_LABELS: Record<string, string> = {
  gmarket: '지마켓',
  auction: '옥션',
  naver: '네이버',
  coupang: '쿠팡',
  st11: '11번가',
  google: '구글',
  youtube: '유튜브',
  instagram: '인스타그램',
  facebook: '페이스북',
  kakao: '카카오',
  daum: '다음',
  direct: '직접 유입',
  referral: '기타 유입',
  [INFLOW_BUCKET_UNKNOWN]: '미수집',
  [INFLOW_BUCKET_MANUAL]: '수동등록',
};

/**
 * 🎨 채널별 색상 (칩/차트 공용)
 * - 브랜드 연상 색을 유지하되 흰 배경 차트에서 검증된 명도로 조정
 * - naver/kakao는 대비가 낮아 라벨·범례·테이블 병행 표기가 필수 (이 앱의 차트는 모두 충족)
 */
export const INFLOW_COLORS: Record<string, string> = {
  gmarket: '#65a30d',
  auction: '#f43f5e',
  naver: '#03c75a',
  coupang: '#ea580c',
  st11: '#d946ef',
  google: '#4285f4',
  youtube: '#dc2626',
  instagram: '#a855f7',
  facebook: '#1e40af',
  kakao: '#ca8a04',
  daum: '#0284c7',
  direct: '#64748b',
  referral: '#14b8a6',
  [INFLOW_BUCKET_UNKNOWN]: '#9ca3af',
  [INFLOW_BUCKET_MANUAL]: '#6366f1', // 기존 신청 목록 '수동' 칩 색상과 통일
};

/**
 * 📋 채널별 한 줄 설명 (관리자 화면 표시용)
 * - 사전 미등록 raw 값과 일반 채널은 getInflowDescription()에서 자동 생성
 */
export const INFLOW_DESCRIPTIONS: Record<string, string> = {
  direct: '주소 직접 입력·즐겨찾기 등 경로 정보 없이 접속한 신청',
  referral: '등록되지 않은 외부 사이트를 거쳐 들어온 신청',
  [INFLOW_BUCKET_UNKNOWN]: '유입 정보가 없는 신청 — 추적 기능 적용 이전 접수 건 등',
  [INFLOW_BUCKET_MANUAL]: '관리자가 직접 등록한 신청 (고객 온라인 유입 아님)',
};

/** 사전 미등록 utm_source용 fallback 색상 (문자열 해시로 결정적 선택) */
const FALLBACK_COLORS = ['#8b5cf6', '#0891b2', '#be185d', '#4d7c0f', '#7c2d12', '#0f766e'];

/** utm_source 별칭 정규화 테이블 (소문자 기준) */
const SOURCE_ALIASES: Record<string, string> = {
  '11st': 'st11',
  '11street': 'st11',
  gmk: 'gmarket',
  insta: 'instagram',
  fb: 'facebook',
  yt: 'youtube',
};

/** referrer 호스트명 → 채널 매핑 규칙 */
const REFERRER_RULES: Array<[RegExp, string]> = [
  [/(^|\.)gmarket\.co\.kr$/, 'gmarket'],
  [/(^|\.)auction\.co\.kr$/, 'auction'],
  [/(^|\.)naver\.(com|me)$/, 'naver'],
  [/(^|\.)coupang\.com$/, 'coupang'],
  [/(^|\.)11st\.co\.kr$/, 'st11'],
  [/(^|\.)google\.[a-z.]+$/, 'google'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)(facebook\.com|fb\.com)$/, 'facebook'],
  [/(^|\.)kakao\.com$/, 'kakao'],
  [/(^|\.)daum\.net$/, 'daum'],
];

/**
 * 🔧 utm_source/ref raw 값 정규화 (trim + 소문자 + 별칭 치환)
 */
export function normalizeInflowSource(raw: string): string {
  const cleaned = raw.trim().toLowerCase();
  return SOURCE_ALIASES[cleaned] ?? cleaned;
}

/**
 * 🔍 referrer 호스트명에서 채널 판별 (미매칭 시 null)
 */
export function resolveSourceFromReferrer(hostname: string): string | null {
  const host = hostname.trim().toLowerCase();
  if (!host) return null;
  for (const [pattern, source] of REFERRER_RULES) {
    if (pattern.test(host)) return source;
  }
  return null;
}

/** 문자열 해시 (fallback 색상 선택용, 렌더 간 결정적) */
function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

/**
 * 🏷️ 채널 라벨 반환 (사전 미등록 raw 값은 그대로 표시)
 */
export function getInflowLabel(source: string | null | undefined): string {
  if (!source) return INFLOW_LABELS[INFLOW_BUCKET_UNKNOWN];
  return INFLOW_LABELS[source] ?? source;
}

/**
 * 🎨 채널 색상 반환 (사전 미등록 raw 값은 해시 기반 fallback 색상)
 */
export function getInflowColor(source: string | null | undefined): string {
  if (!source) return INFLOW_COLORS[INFLOW_BUCKET_UNKNOWN];
  return INFLOW_COLORS[source] ?? FALLBACK_COLORS[hashString(source) % FALLBACK_COLORS.length];
}

/**
 * 📋 채널 한 줄 설명 반환
 * - 특수 버킷(direct/referral/미수집/수동등록)은 사전 정의 문구
 * - 알려진 채널은 "지마켓 배너·링크를 통해 유입된 신청" 형태로 생성
 * - 사전 미등록 raw 값은 utm_source 값을 그대로 안내
 */
export function getInflowDescription(source: string | null | undefined): string {
  if (!source) return INFLOW_DESCRIPTIONS[INFLOW_BUCKET_UNKNOWN];
  if (INFLOW_DESCRIPTIONS[source]) return INFLOW_DESCRIPTIONS[source];
  if (INFLOW_LABELS[source]) return `${INFLOW_LABELS[source]} 배너·링크를 통해 유입된 신청`;
  return `추적 링크(utm_source=${source})를 통해 유입된 신청`;
}

/** 마케팅 성과 판단에서 제외하는 표시 전용 버킷 여부 (미수집/수동등록) */
export function isAuxiliaryBucket(bucket: string): boolean {
  return bucket === INFLOW_BUCKET_UNKNOWN || bucket === INFLOW_BUCKET_MANUAL;
}

/**
 * 📊 신청 문서의 유입경로 표시 버킷 계산
 * - inflow.source 있으면 그 값
 * - 관리자 수동/거래처 등록 건은 '수동등록'
 * - 그 외(레거시 문서 포함)는 '미수집'
 */
export function getInflowBucket(app: {
  inflow?: { source?: string } | null;
  source_type?: string | null;
}): string {
  if (app.inflow?.source) return app.inflow.source;
  if (app.source_type === 'manual' || app.source_type === 'partner') return INFLOW_BUCKET_MANUAL;
  return INFLOW_BUCKET_UNKNOWN;
}
